import { env } from '@toggle/helpers';

interface RuntimeConfig {
  WS_ROOT?: string;
  API_ROOT?: string;
  AUTH_ROOT?: string;
  PUBLIC_POSTHOG_HOST?: string;
  IS_PRIVATE_NETWORK?: string;
  DEEP_LINK_BASE_URL?: string;
  DEEP_LINK_ENTITY_PATH?: string;
  APP_BASE_PATH?: string;
  IMG_ROOT?: string;
}

declare global {
  interface Window {
    __RUNTIME_CONFIG__?: RuntimeConfig;
  }
}

const rc = window.__RUNTIME_CONFIG__;

const hostUrl = new URL(document.URL).origin;

const environment = env(process.env.V2_ENV_LEVEL, 'staging');

const privateNetworkUrl = rc?.DEEP_LINK_BASE_URL;
const isPrivateNetwork = rc?.IS_PRIVATE_NETWORK;
// Deep links are served from the same proxy as the private network, so the base
// host is taken from DEEP_LINK_BASE_URL (set without a trailing slash), falling
// back to the public PPE Workspace URL for local/dev builds.
const deepLinkBaseUrl = env(
  rc?.DEEP_LINK_BASE_URL,
  env(process.env.DEEP_LINK_BASE_URL, 'https://workspace.ppe.refinitiv.com')
);

// The entity deep link is the base host plus an app path that ends in the query
// parameter the RIC is appended to (see EntityItem and resolveNewsLinkUrl).
// Overridable per deployment because the Workspace app hosting the entity
// overview differs between Workspace releases; the value must keep a trailing
// `s=`.
const configuredDeepLinkEntityPath = env(
  rc?.DEEP_LINK_ENTITY_PATH,
  process.env.DEEP_LINK_ENTITY_PATH
);
const deepLinkEntityPath = env(
  configuredDeepLinkEntityPath,
  '/web/Apps/Corp/?st=RIC&s='
);

// The base path the app is served under. Driven at container runtime via
// window.__RUNTIME_CONFIG__ (see docker/docker-entrypoint.sh), falling back to
// the build-time env and finally the hard-coded default. Kept without a trailing
// slash so it can be concatenated with absolute paths (e.g. `${basePath}/login`).
const appBasePath = env(
  rc?.APP_BASE_PATH,
  env(process.env.APP_BASE_PATH, '/DeepResearchAIUI')
);

const imageRoot = env(rc?.IMG_ROOT, env(process.env.IMG_ROOT, '')).replace(
  /\/+$/,
  ''
);

export const config = {
  posthog: {
    apiKey: env(
      process.env.PUBLIC_POSTHOG_KEY,
      'phc_fEGrfTrcf1GQsGjyo7lhr6E3bGXqM4NKUugLI9kZaMq'
    ),
    apiHost: env(
      rc?.PUBLIC_POSTHOG_HOST,
      env(process.env.PUBLIC_POSTHOG_HOST, 'https://ph.lseg.reflexivity.com/')
    ),
    uiHost: 'https://us.posthog.com',
  },
  app: {
    version: env(process.env.V2_VERSION, ''),
    environment,
    isLocalEnvironment: hostUrl === 'http://localhost:1234',
    featureFlags: env(process.env.V2_FEATURE_FLAGS, '')?.split(','),
    privateNetworkUrl,
    isPrivateNetwork,
    basePath: appBasePath,
  },
  release: {
    date: env(process.env.V2_RELEASE_DATE, new Date().toISOString()),
    number: env(process.env.V2_RELEASE_NUMBER, '1.1.0'),
  },
  api: {
    root: env(
      rc?.API_ROOT,
      env(process.env.API_ROOT, 'https://api.lseg.rflx.co.uk/')
    ),
    authRoot: env(
      rc?.AUTH_ROOT,
      env(process.env.AUTH_ROOT, 'https://auth.lseg.rflx.co.uk/')
    ),
    wsRoot: env(
      rc?.WS_ROOT,
      env(process.env.WS_ROOT, 'wss://ws.lseg.rflx.co.uk/')
    ),
  },
  images: {
    root: imageRoot,
  },
  ws: {
    reconnectionLimit: Number(env(process.env.V2_WS_RECONNECTION_LIMIT, '5')),
    reconnectionTimeout: Number(
      env(process.env.V2_WS_RECONNECTION_TIMEOUT, '500')
    ),
    pollingInterval: Number(env(process.env.V2_WS_POLLING_INTERVAL, '5000')),
    connectTimeout: Number(env(process.env.V2_WS_CONNECT_TIMEOUT, '10000')),
  },
  deepLink: {
    publicWorkspaceUrl: env(
      process.env.DEEP_LINK_BASE_URL,
      'https://workspace.ppe.refinitiv.com'
    ), // Keep this public so non-entity news links from the BE can be remapped.
    conversation: `${deepLinkBaseUrl}/web/rap/deepresearch`,
    entity: `${deepLinkBaseUrl}${deepLinkEntityPath}`,
    entityOverride: configuredDeepLinkEntityPath
      ? `${deepLinkBaseUrl}${configuredDeepLinkEntityPath}`
      : '',
    //multiple rics example LSEG.L&MMM.N&CMG.N&CAVA.K
    chart: `${deepLinkBaseUrl}/web/rap/financial-chart/index.html?s=`,
    workspaceAi: `${deepLinkBaseUrl}/web/rap/workspace-ai-app/`,
  },
  urls: {
    root: hostUrl,
    lsegStatement: 'https://www.lseg.com/en/policies/privacy-statement',
    faqs: `${appBasePath}/static/docs/Workspace Deep Research FAQ 3.pdf`,
  },
  ms: {
    teamsParentUrl: env(process.env.TEAMS_PARENT_URL, ''),
  },
  supportEmail: 'WorkspaceChatEAP@lseg.com',
};
